import React, { FC, useMemo } from 'react';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { BodyTextSmall, pxToRem } from '@modernhealth/vital-components';
import { colors as dsColors } from '@modernhealth/vital-foundation';
import { useIntl } from 'react-intl';

import { getSessionUtilizationInfo } from 'src/modules/videoSessions/selectors/videoSessionsSelectors';
import { getUserInformation } from 'src/modules/user/selectors/userSelectors';

import { hexToRgba } from 'src/shared/helpers/hexToRgba';

export const ForYouModalInfoBar: FC = () => {
    const { t } = useTranslation();
    const intl = useIntl();
    const { coachingVisitsRemaining, therapyVisitsRemaining, resetDate, coveredTherapyVisits } =
        useSelector(getSessionUtilizationInfo);
    const user = useSelector(getUserInformation);

    const displayText = useMemo(() => {
        const date = intl.formatDate(resetDate.toDate(), {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
        });
        const shouldShowNoRemainingSessionsMsg =
            coachingVisitsRemaining === 0 &&
            therapyVisitsRemaining === 0 &&
            !(user.isCompanyHpiEnabled && user.hpiEligibilityStatus);

        if (shouldShowNoRemainingSessionsMsg) {
            return t('dashboard:for_you.no_remaining_sessions', {
                date,
            });
        }

        // when a member is HPI eligible
        const shouldShowHpiEligibilityMsg =
            therapyVisitsRemaining === 0 && user.isCompanyHpiEnabled && user.hpiEligibilityStatus;

        if (shouldShowHpiEligibilityMsg) {
            return t('dashboard:for_you.remaining_sessions_with_hpi_eligibility', {
                coachingVisitsRemaining,
                date,
            });
        }

        if (coveredTherapyVisits > 0) {
            return t('dashboard:for_you.remaining_sessions_with_therapy', {
                coachingVisitsRemaining,
                therapyVisitsRemaining,
                date,
            });
        }

        return t('dashboard:for_you.remaining_sessions_coaching_only', {
            coachingVisitsRemaining,
            date,
        });
    }, [
        coachingVisitsRemaining,
        coveredTherapyVisits,
        resetDate,
        therapyVisitsRemaining,
        t,
        user,
        intl,
    ]);

    return (
        <InfoBar>
            <BodyTextSmall>{displayText}</BodyTextSmall>
        </InfoBar>
    );
};

const InfoBar = styled.div({
    backgroundColor: hexToRgba(dsColors.gray05, 0.5),
    borderRadius: pxToRem(4),
    marginBottom: pxToRem(16),
    padding: pxToRem(12),
});
